import { Dispatch, SetStateAction } from "react";
import { RequestServer } from "../../../utils/services";
import { APIRoutes } from "../../../constants";
import {
  AgendaGetApiResponse,
  SetAgendaDataState,
} from "../../../pages/Projects/types";
import {
  ModalStates,
  agenda_response,
} from "../../project-detail/project-detail-card/helper";
import { EnqueueSnackbar } from "notistack";
import { copyHtml } from "../../../utils/copyHtml";

export const submitAgendaResponse = async (
  response: saveAgendaResponse,
  pe_id: number,
  handleSubmitClose: () => void,
  setResponse: Dispatch<SetStateAction<saveAgendaResponse>>,
  setBackdrop: (b: boolean) => void,
  enqueueSnackbar: EnqueueSnackbar
) => {
  try {
    setBackdrop(true);
    const payload = {
      action: "RespondToAgenda",
      id: pe_id, // pe_id,
      agenda_response: response,
    };
    const res = await RequestServer(APIRoutes.peMapping, "PATCH", payload);
    if (res.success) {
      enqueueSnackbar(res.message, {
        variant: "success",
      });
      handleSubmitClose();
      setResponse(response);
    } else {
      console.log(res);
      enqueueSnackbar(res.message || res.error || "something went wrong", {
        variant: "warning",
      });
    }
  } catch (err) {
    console.log(err);
  } finally {
    setBackdrop(false);
  }
};

export const getAgendaId = async (projectId: string) => {
  try {
    const projectResponse = await RequestServer(
      APIRoutes.projects +
        "?id=" +
        projectId +
        "&show_columns=applicable_agenda_id",
      "GET"
    );

    return projectResponse.data[0].applicable_agenda_id;
  } catch (err) {
    console.log(err);
  }
};

export const getAgendaData = async (
  setData: SetAgendaDataState,
  projectId: string
) => {
  try {
    const response: AgendaGetApiResponse = await RequestServer(
      APIRoutes.peMapping +
        "?fk_project=" +
        projectId +
        "&show_columns=agenda_responses,fk_agenda,agenda_shared_on,id,expert_name,meta,agenda_status&notnull___agenda_shared_on=1",
      "GET"
    );
    if (response.success) {
      const data = response.data;
      // sort expert name by alphabetically
      const sortedData = data.sort((a, b) => {
        const nameA = a.expert_name.trim().toLowerCase();
        const nameB = b.expert_name.trim().toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });

      const applicable_agenda_id = await getAgendaId(projectId);

      setData((prev) => ({
        ...prev,
        agenda: sortedData,
        applicable_agenda_id,
      }));
    }
  } catch (err) {
    console.log(err);
  }
};

export const setApplicableAgendaId = async (
  projectId: string,
  setData: SetAgendaDataState
) => {
  try {
    const applicable_agenda_id = await getAgendaId(projectId);
    console.log("applicable_agenda_id", applicable_agenda_id);
    setData((prev) => ({
      ...prev,
      applicable_agenda_id,
    }));
  } catch (err) {
    console.log(err);
  }
};

export type saveAgendaResponse = agenda_response[];


export enum GetAgendaEnum {
  AGENDA_WITH_ANSWER = 'GetAgendaWithAnswer'
}

const isAgendaWithAnswerResponse = (data: any): data is AgendaQuesResponseWithAnswer["data"] => {
  return Array.isArray(data.agenda);
};
type AgendaQuesResponseWithAnswer = {
  success: boolean;
  message: string;
  data: {
    agenda: agenda_response[];
  };
};

type AgendaQuesResponseWhenOnlyQuestion = {
  success: boolean;
  message: string;
  data: {
    agenda: {
      questions: string[];
    };
  }[];
};
export const getAgendaQues = async (
  id: number,
  setResponse: Dispatch<SetStateAction<saveAgendaResponse>>,
  action?: GetAgendaEnum
): Promise<void> => {
  try {
    let url: string;

    if (action) {
      url = `${APIRoutes.agenda}?id=${id}&action=${action}`;
    } else {
      url = `${APIRoutes.agenda}?id=${id}&show_columns=agenda`;
    }

    const response: AgendaQuesResponseWithAnswer | AgendaQuesResponseWhenOnlyQuestion = await RequestServer(url, "get");

    if (response.success) {
      let ques: saveAgendaResponse = [];

      if (action === GetAgendaEnum.AGENDA_WITH_ANSWER) {
        if (isAgendaWithAnswerResponse(response.data)) {
          ques = response.data.agenda.map((agendaObj) => ({
            question: agendaObj.question,
            answer: agendaObj.answer,
            expert_note: agendaObj.expert_note,
          }));
        }
      } else {
        if (Array.isArray(response.data)) {
          const questions = response.data[0]?.agenda?.questions || [];
          ques = questions.map((ques) => ({
            question: ques,
            answer: "",
            expert_note: "",
          }));
        }
      }

      setResponse(ques);
    }
  } catch (err) {
    console.log(err);
  }
};

export const shareAgendaResponseAns = [
  { label: "No Idea", value: "No Idea" },
  { label: "Not Comfortable", value: "Not Comfortable" },
  { label: "Neutral", value: "Neutral" },
  { label: "Comfortable", value: "Comfortable" },
  { label: "Very Comfortable", value: "Very Comfortable" },
];

export interface Project_Item {
  applicable_agenda_id: null | string;
  id: number;
}

function generateCopyContent(agenda_responses: ModalStates["isAgendaDescription"]["agenda_responses"]): HTMLElement {
  // Create a container for the copy content
  const container = document.createElement("div");
  if (!agenda_responses) {
    return container; 
  }
  agenda_responses.forEach((response, index) => {
    const block = document.createElement("div");
    block.style.marginBottom = "1rem";

    // Line 1: Q{index}. {question} (question is optional)
    const line1 = document.createElement("div");
    const questionText = response.question ? ` ${response.question}` : "";
    line1.innerHTML = `<strong>Q${index + 1}.${questionText}</strong>`;
    block.appendChild(line1);

    // Line 2: Expert Response - {answer} (answer in green)
    if (response.answer) {
      const line2 = document.createElement("div");
      line2.innerHTML = `<strong>Expert Response - </strong><span style="color: #099A51">${response.answer}</span>`;
      block.appendChild(line2);
    }

    // Line 3 (optional): Expert Note - {expert_note}
    if (response.expert_note) {
      const line3 = document.createElement("div");
      line3.innerHTML = `<strong>Expert Note - </strong><span>${response.expert_note}</span>`;
      block.appendChild(line3);
    }

    // Append this block to the container
    container.appendChild(block);
  });

  return container;
}



  export const handleDirectCopyAgendaToClipboard = async (agenda_responses:ModalStates["isAgendaDescription"]["agenda_responses"], enqueueSnackbar: any ) => {
    const copyContent = generateCopyContent(agenda_responses);
  
    const tempRef: React.RefObject<HTMLDivElement> = {
      current: copyContent as HTMLDivElement,
    };
  
    await copyHtml(tempRef, enqueueSnackbar);
  };

  export async function shareAgendaHandler(
    pe_id: number | null,
    enqueueSnackbar: EnqueueSnackbar,
    handleClose: () => void,
    refetch: () => Promise<void>,
  ) {
    if (!pe_id) {
      return;
    }
  
    try {
      const payload = {
        action: "ShareAgenda",
        id: pe_id,
      };
  
      const response = await RequestServer(APIRoutes.peMapping, "PATCH", payload);
  
      if (response.success) {
        enqueueSnackbar(response.message, {
          variant: "success",
        });
        handleClose()
        refetch()
      } else {
        console.log({ response });
        enqueueSnackbar(response.message, {
          variant: "warning",
        });
      }
    } catch (err) {
      console.log(err);
    } 
  }

  export function getFilteredProjectAgendaData(projectAgendaData: any, agendaResponseFilter: string) {
    if (!projectAgendaData || !projectAgendaData.agenda) return null;
  
    if (agendaResponseFilter === 'default') {
      return projectAgendaData;
    }
  
    const clonedData = structuredClone(projectAgendaData);
    const sortedAgenda = [...clonedData.agenda];
  
    sortedAgenda.sort((a, b) => {
      const aHasResponses = !!a.agenda_responses && a.agenda_responses.length > 0;
      const bHasResponses = !!b.agenda_responses && b.agenda_responses.length > 0;
  
      if (agendaResponseFilter === 'completed') {
        return Number(!aHasResponses) - Number(!bHasResponses); // responses first
      } else if (agendaResponseFilter === 'pending') {
        return Number(aHasResponses) - Number(bHasResponses); // no responses first
      }
      return 0;
    });
  
    return {
      ...clonedData,
      agenda: sortedAgenda,
    };
  }

  export const filterExpertBasedOnAgendaResponsesOptions = [
    { value: "default", label: "Default" },
    { value: "completed", label: "Completed" },
    { value: "pending", label: "Pending" },
  ]